/**
 * Custom hook for managing streaming API requests
 */

import { useState, useCallback, useRef } from 'react';

export function useStreamingRequest() {
  const [isPending, setIsPending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const streamingContentRef = useRef<string>('');

  const startStreaming = useCallback(() => {
    const controller = new AbortController();
    setAbortController(controller);
    setIsPending(true);
    setIsStreaming(true);
    streamingContentRef.current = '';
    return controller;
  }, []);

  const stopStreaming = useCallback(() => {
    if (abortController) {
      abortController.abort();
    }
    setAbortController(null);
    setIsPending(false);
    setIsStreaming(false);
  }, [abortController]);

  const finishStreaming = useCallback(() => {
    setAbortController(null);
    setIsPending(false);
    setIsStreaming(false);
  }, []);

  const resetStreamingContent = useCallback(() => {
    streamingContentRef.current = '';
  }, []);

  return {
    isPending,
    isStreaming,
    abortController,
    streamingContentRef,
    startStreaming,
    stopStreaming,
    finishStreaming,
    resetStreamingContent,
    setIsPending
  };
}
